import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../data";
import { useAction } from "../../components/ui";
import { loadGroupOverview } from "../agentur/einstellungen/data";
import type { OrgOverviewRow } from "./model";

/**
 * Enterprise-Verbund (0174) in der Betreiber-Konsole: Verbund anlegen (diese Agentur als Hauptagentur), Agentur einem Verbund
 * hinzufügen (im Normalfall zum Laufzeitende ihres eigenen Abos, auf Wunsch sofort), Standort entfernen, Gruppen-Admins setzen.
 * Die Datenbank prüft selbst, dass nur Plattform-Admins das dürfen.
 */
export function VerbundSteuerung({ row }: { row: OrgOverviewRow }) {
  const orgId = row.org_id;
  const g = useQuery({ queryKey: ["org_group", orgId, "betrieb"], queryFn: () => loadGroupOverview(api, orgId) });
  const gruppen = useQuery({
    queryKey: ["org_groups", "betrieb"],
    queryFn: () => api.select<{ id: string; name: string; billing_org_id: string }>("org_groups", { columns: "id, name, billing_org_id", order: [["name", "asc"]] }),
  });
  const { busy, error, run } = useAction();
  const [name, setName] = useState(row.name);
  const [ziel, setZiel] = useState("");
  const [sofort, setSofort] = useState(false);
  const [email, setEmail] = useState("");
  const [rolle, setRolle] = useState<"group_admin" | "group_owner">("group_admin");
  const [entfernen, setEntfernen] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);

  if (g.isPending) return <section className="panel stack"><h2>Verbund</h2><p>Wird geladen …</p></section>;
  if (g.error) return <section className="panel stack"><h2>Verbund</h2><p className="tk-error">{(g.error as Error).message}</p></section>;
  const v = g.data;

  if (!v) {
    const wahl = (gruppen.data ?? []).filter((x) => x.billing_org_id !== orgId);
    return (
      <section className="panel stack" aria-labelledby="verbund-titel">
        <h2 id="verbund-titel">Verbund</h2>
        <p>Diese Agentur gehört zu keinem Verbund. Verbünde entstehen nach dem Enterprise-Gespräch: die Hauptagentur trägt das Abo, Grenzen gelten als Summe.</p>
        <label>Name des neuen Verbunds<input value={name} onChange={(e) => setName(e.target.value)} /></label>
        <div className="row">
          <button type="button" disabled={name.trim().length < 2 || !!busy} aria-busy={busy === "anlegen"}
            onClick={() => void run("anlegen", () => api.rpc("platform_group_create", { p_name: name.trim(), p_billing_org: orgId }), "Verbund angelegt; diese Agentur ist die Hauptagentur.")}>
            {busy === "anlegen" ? "Wird angelegt …" : "Verbund mit dieser Agentur als Hauptagentur anlegen"}
          </button>
        </div>
        {wahl.length > 0 && (
          <>
            <label>
              Oder einem bestehenden Verbund hinzufügen
              <select value={ziel} onChange={(e) => setZiel(e.target.value)}>
                <option value="">Verbund wählen</option>
                {wahl.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </label>
            <label className="row tk-check">
              <input type="checkbox" checked={sofort} onChange={(e) => setSofort(e.target.checked)} />
              <span>Sofort aufnehmen, auch wenn ein eigenes Abo läuft (anteilige Erstattung dann selbst in Stripe bzw. PayPal)</span>
            </label>
            <div className="row">
              <button type="button" disabled={!ziel || !!busy} aria-busy={busy === "aufnehmen"}
                onClick={() => void run("aufnehmen", () => api.rpc<string>("platform_group_add_org", { p_group: ziel, p_org: orgId, p_sofort: sofort }))
                  .then((r) => r && setHinweis(r === "pending" ? "Aufnahme vorgemerkt: die Agentur kommt in den Verbund, sobald ihr eigenes Abo endet." : "Agentur in den Verbund aufgenommen."))}>
                {busy === "aufnehmen" ? "Wird aufgenommen …" : "Zum Verbund hinzufügen"}
              </button>
            </div>
          </>
        )}
        {hinweis && <p role="status">{hinweis}</p>}
        {error && <p className="tk-error" role="alert">{error}</p>}
      </section>
    );
  }

  const haupt = v.billing_org_id === orgId;
  return (
    <section className="panel stack" aria-labelledby="verbund-titel">
      <h2 id="verbund-titel">Verbund {v.name}</h2>
      <p>
        {haupt ? "Hauptagentur: trägt das Abo für alle Standorte." : `Standort; Abo über ${v.billing_org_name ?? "die Hauptagentur"}.`}<br />
        Creator {v.talents} / {v.max_talents ?? "–"} · Team-Plätze {v.seats} / {v.max_seats ?? "–"} (Summe der Gruppe)
      </p>
      {v.orgs && <p>Standorte: {v.orgs.map((o) => `${o.name}${o.is_billing ? " (Haupt)" : ""}`).join(", ")}</p>}
      {v.pending && v.pending.length > 0 && <p>Aufnahme vorgemerkt: {v.pending.map((p) => p.name).join(", ")}</p>}
      <h3>Gruppen-Admins</h3>
      <ul>
        {(v.admins ?? []).map((a) => (
          <li key={a.user_id} className="row between">
            <span>{a.email} <small>{a.role === "group_owner" ? "Eigentümer" : "Admin"}</small></span>
            <button type="button" className="quiet" disabled={!!busy} onClick={() => void run(`admin-${a.user_id}`, () => api.rpc("platform_group_remove_admin", { p_group: v.id, p_user: a.user_id }), "Gruppen-Admin entfernt.")}>Entfernen</button>
          </li>
        ))}
        {(v.admins ?? []).length === 0 && <li>Noch keine.</li>}
      </ul>
      <div className="row">
        <label>E-Mail (bestehendes Konto)<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" /></label>
        <label>Rolle
          <select value={rolle} onChange={(e) => setRolle(e.target.value as "group_admin" | "group_owner")}>
            <option value="group_admin">Admin</option>
            <option value="group_owner">Eigentümer</option>
          </select>
        </label>
      </div>
      <div className="row">
        <button type="button" disabled={!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) || !!busy} aria-busy={busy === "admin"}
          onClick={() => void run("admin", () => api.rpc("platform_group_set_admin", { p_group: v.id, p_email: email.trim(), p_role: rolle }), "Gruppen-Admin gesetzt; Admin in allen Standorten.").then((r) => r !== undefined && setEmail(""))}>
          {busy === "admin" ? "Wird gesetzt …" : "Gruppen-Admin setzen"}
        </button>
      </div>
      {!haupt && (
        entfernen ? (
          <div className="notice warning stack" role="alert">
            <p>Die Agentur verlässt den Verbund und braucht ab sofort ein eigenes Abo: sieben Tage Testphase mit Zahlungsschritt. Gruppen-Admins verlieren hier ihren Zugang.</p>
            <div className="row">
              <button type="button" className="primary" disabled={!!busy} aria-busy={busy === "entfernen"}
                onClick={() => void run("entfernen", () => api.rpc("platform_group_remove_org", { p_org: orgId }), "Agentur aus dem Verbund entfernt.").then(() => setEntfernen(false))}>
                {busy === "entfernen" ? "Wird entfernt …" : "Endgültig aus dem Verbund entfernen"}
              </button>
              <button type="button" onClick={() => setEntfernen(false)}>Abbrechen</button>
            </div>
          </div>
        ) : (
          <div className="row"><button type="button" onClick={() => setEntfernen(true)}>Aus dem Verbund entfernen …</button></div>
        )
      )}
      {error && <p className="tk-error" role="alert">{error}</p>}
    </section>
  );
}
